import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';

const testId = createTestIdHelper('notification-dot');

export interface NotificationDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Text announced by assistive technology the moment the dot appears
   * (e.g. "You have unread messages"). The dot is a `status` live region, so
   * this doubles as the non-visual signal — the indicator is never conveyed by
   * colour alone.
   */
  label: string;
}

/**
 * A small red presence indicator meaning "there is something new here" —
 * unread messages, pending notifications. Purely presentational: the caller
 * decides *when* to render it and positions it (typically absolutely over an
 * icon via `className`).
 *
 * Colour uses the semantic `destructive` token — the design system's only red
 * — and the ring defaults to the page surface (`ring-background`) so the dot
 * stays legible against a busy icon; override `ring-*` when placed on a
 * non-default surface (e.g. `ring-sidebar`).
 */
export const NotificationDot = React.forwardRef<
  HTMLSpanElement,
  NotificationDotProps
>(({ label, className, ...props }, ref) => (
  <span
    ref={ref}
    role="status"
    aria-label={label}
    data-testid={testId('root')}
    className={cn(
      'size-2.5 rounded-full bg-destructive ring-2 ring-background',
      className,
    )}
    {...props}
  />
));

NotificationDot.displayName = 'NotificationDot';
